import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { Skeleton } from 'primeng/skeleton';

import { InputDateInterface } from '../../../types/interfaces/input.interface';
import { TranslateSafePipe } from '../../pipes/translation.pipe';

type DatePickerStyle = Record<string, string>;

@Component({
  selector: 'app-input-date',
  imports: [
    ReactiveFormsModule,
    FloatLabel,
    DatePicker,
    Skeleton,
    TranslateSafePipe,
  ],
  templateUrl: './input-date.component.html',
  styleUrls: [
    '../../styles/input-base.css',
    './input-date.component.css',
  ],
})
export class InputDateComponent implements OnInit, OnDestroy {
  @Input()
  public formData!: InputDateInterface;

  @Input()
  public isLoading: boolean = false;

  public datePickerRootStyle: DatePickerStyle = {};
  public datePickerInputStyle: DatePickerStyle = {};
  public datePickerPanelStyle: DatePickerStyle = {};

  private _themeObserver: MutationObserver | null = null;

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  public ngOnInit(): void {
    this.initDatePickerTheme();
    this.observeThemeChanges();
  }

  public ngOnDestroy(): void {
    this._themeObserver?.disconnect();
  }

  public checkError(): boolean {
    if (!this.formData.formGroup) {
      return false;
    }

    const formControl = this.formData.formGroup.get(this.formData.id);
    const isTouched: boolean = formControl?.touched === true;

    return isTouched && formControl?.hasError('required') || false;
  }

  public onDateSelect(_date: Date): void {
    if (this.formData.onInput) {
      this.formData.onInput();
    }
  }

  public onClear(): void {
    if (this.formData.onInput) {
      this.formData.onInput();
    }
  }

  private observeThemeChanges(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._themeObserver = new MutationObserver(() => {
      this.initDatePickerTheme();
    });

    this._themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: [
        'data-theme',
      ],
    });
  }

  private initDatePickerTheme(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    const bodyStyle: CSSStyleDeclaration = getComputedStyle(document.body);
    const rootStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement);

    const backgroundColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg',
        '--glass-bg-1',
        '--surface-card',
      ],
      '#ffffff',
    );

    const strongBackgroundColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg-2',
        '--surface-ground',
      ],
      '#f8fafc',
    );

    const hoverBackgroundColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg-3',
        '--surface-hover',
      ],
      '#e5e7eb',
    );

    const borderColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--glass-bg-4',
        '--glass-border',
        '--border-color',
      ],
      'rgba(17, 24, 39, 0.16)',
    );

    const textColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--text-color',
        '--text-primary',
        '--app-text-color',
      ],
      '#111827',
    );

    const mutedTextColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--text-secondary',
        '--text-muted',
      ],
      '#6b7280',
    );

    const primaryColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--primary-color',
        '--p-primary-color',
      ],
      '#3b82f6',
    );

    const primaryTextColor: string = this.getCssVariable(
      bodyStyle,
      rootStyle,
      [
        '--primary-contrast-color',
        '--p-primary-contrast-color',
      ],
      '#ffffff',
    );

    this.datePickerRootStyle = {
      width: '100%',

      '--p-datepicker-dropdown-background': strongBackgroundColor,
      '--p-datepicker-dropdown-hover-background': hoverBackgroundColor,
      '--p-datepicker-dropdown-active-background': hoverBackgroundColor,
      '--p-datepicker-dropdown-border-color': borderColor,
      '--p-datepicker-dropdown-hover-border-color': primaryColor,
      '--p-datepicker-dropdown-active-border-color': primaryColor,
      '--p-datepicker-dropdown-color': textColor,
      '--p-datepicker-dropdown-hover-color': textColor,
      '--p-datepicker-dropdown-active-color': textColor,
      '--p-datepicker-input-icon-color': textColor,
    };

    this.datePickerInputStyle = {
      background: backgroundColor,
      color: textColor,
      borderColor,
    };

    this.datePickerPanelStyle = {
      background: backgroundColor,
      color: textColor,
      borderColor,

      '--p-datepicker-panel-background': backgroundColor,
      '--p-datepicker-panel-border-color': borderColor,
      '--p-datepicker-panel-color': textColor,

      '--p-datepicker-header-background': backgroundColor,
      '--p-datepicker-header-border-color': borderColor,
      '--p-datepicker-header-color': textColor,

      '--p-datepicker-select-month-color': textColor,
      '--p-datepicker-select-month-hover-background': hoverBackgroundColor,
      '--p-datepicker-select-month-hover-color': textColor,

      '--p-datepicker-select-year-color': textColor,
      '--p-datepicker-select-year-hover-background': hoverBackgroundColor,
      '--p-datepicker-select-year-hover-color': textColor,

      '--p-datepicker-week-day-color': mutedTextColor,

      '--p-datepicker-date-color': textColor,
      '--p-datepicker-date-hover-background': hoverBackgroundColor,
      '--p-datepicker-date-hover-color': textColor,

      '--p-datepicker-date-selected-background': primaryColor,
      '--p-datepicker-date-selected-color': primaryTextColor,

      '--p-datepicker-date-range-selected-color': textColor,

      '--p-datepicker-today-color': textColor,

      '--p-datepicker-buttonbar-border-color': borderColor,
      '--p-datepicker-time-picker-border-color': borderColor,
    };

    this._changeDetectorRef.markForCheck();
  }

  private getCssVariable(
    bodyStyle: CSSStyleDeclaration,
    rootStyle: CSSStyleDeclaration,
    variableNames: string[],
    fallback: string,
  ): string {
    for (const variableName of variableNames) {
      const bodyValue: string = bodyStyle.getPropertyValue(variableName)
        .trim();

      if (bodyValue !== '') {
        return bodyValue;
      }

      const rootValue: string = rootStyle.getPropertyValue(variableName)
        .trim();

      if (rootValue !== '') {
        return rootValue;
      }
    }

    return fallback;
  }
}